import { useId, type Ref } from 'react';

import Link from 'next/link';
import { useTranslations } from 'next-intl';

import BazakLogo from '@/components/bazak-logo';
import { NewTabHintText, newTabLabelling } from '@/components/ui/new-tab-hint-text';
import { cn } from '@/lib/utils';

/** "Powered by" and the Bazak mark, linking out. Named by its label even when the text is
 *  hidden for width, where the logo alone would leave the link nameless. The new-tab hint comes
 *  from next-intl, not `NewTabHintProvider`, because the verify page renders this on the server. */
export function PoweredByBazak({
  href,
  showText = true,
  className,
  ref,
}: {
  href: string;
  showText?: boolean;
  className?: string;
  ref?: Ref<HTMLAnchorElement>;
}) {
  const t = useTranslations();
  const label = t('disclaimer.poweredByText');
  const ariaLabel = `${label} Bazak`;
  const hint = t('accessibility.opensInNewTab');
  const generatedId = useId();
  const { linkProps, hintId } = newTabLabelling({ ariaLabel, hint, generatedId });
  return (
    <Link
      ref={ref}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      {...linkProps}
      className={cn(
        'flex shrink-0 items-center gap-1.5 transition-colors hover:text-foreground',
        className,
      )}
    >
      <span className={cn('leading-none', !showText && 'hidden')}>{label}</span>
      <BazakLogo aria-hidden className="h-3 w-auto" />
      <NewTabHintText id={hintId} hint={hint} />
    </Link>
  );
}
